import { useClassContext, ClassContext } from 'components/class-context'
import { useContext } from 'react'
import styles from 'css/components/action-dropdown.module.css'
import copyIcon from 'img/dashicons-admin-page.svg'
import downloadIcon from 'img/dashicons-download.svg'
import expandIcon from 'img/dashicons-editor-expand.svg'
import editIcon from 'img/edit.svg'
import trashIcon from 'img/dashicons-trash.svg'
import uploadFileIcon from 'img/upload-cloud-blue.svg';
import React from 'react'

interface ActionDropdownProps {
    actions: {
        label: string
        onClick: () => void
        // Optional: a destructive action carries its meaning in the label and
        // its styling, not an icon.
        icon?: React.ReactNode
        disabled?: boolean
        /** Destructive. Styled apart so it doesn't read as a routine action. */
        danger?: boolean
    }[]
    className?: string
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ actions, className }) => {
    return (
        <div className={`${styles.actionDropdown} ${className}`}>
            {actions.map((action) => (
                <div 
                    key={action.label} 
                    onClick={action.disabled ? undefined : action.onClick} 
                    className={`${styles.actionDropdown__item} ${action.disabled ? styles.actionDropdown__item__disabled : ''} ${action.danger ? styles.actionDropdown__item__danger : ''}`}
                >
                    {action.icon && <span className={styles.actionDropdown__item__icon}>{action.icon}</span>}
                    {action.disabled ? `${action.label}...` : action.label}
                </div>
            ))}
        </div>
    )
}

export default ActionDropdown

export const ClassActionDropdown = ({
    onOpenStudent,
    onRemoveStudent,
}: {
    onOpenStudent: () => void,
    // Optional on purpose: a caller with no way to refresh the roster afterwards
    // simply does not pass it and the action does not appear.
    onRemoveStudent?: () => void,
}) => {
    const { handleDownloadXLS, handleDownloadPDF } = useClassContext()

    return (
        <ActionDropdown actions={[
            { label: 'Open Student Data', onClick: onOpenStudent, icon: <img src={expandIcon} alt="Open Student Data" /> },
            { label: 'Download in XLS', onClick: handleDownloadXLS, icon: <img src={downloadIcon} alt="Download in XLS" /> },
            { label: 'Download in PDF', onClick: handleDownloadPDF, icon: <img src={downloadIcon} alt="Download in PDF" /> },
            ...(onRemoveStudent ? [{
                label: 'Remove student',
                onClick: onRemoveStudent,
                danger: true,
            }] : []),
        ]} className={styles.classActionDropdown} />
    )
}

interface DownloadActionDropdownProps {
    onDownloadXLS?: () => void
    onDownloadPDF?: () => void
    // Optional on purpose. The student-results and student-overview pages use
    // this dropdown too and have no roster to print, so they simply do not
    // pass it and the action does not appear for them.
    onPrintRoster?: () => void
}

export const DownloadActionDropdown = ({ onDownloadXLS, onDownloadPDF, onPrintRoster }: DownloadActionDropdownProps = {}) => {
    const classContext = useContext(ClassContext)
    const handleXLS = onDownloadXLS ?? classContext?.handleDownloadXLS ?? (() => {})
    const handlePDF = onDownloadPDF ?? classContext?.handleDownloadPDF ?? (() => {})

    const actions = [
        { label: 'Download in XLS', onClick: handleXLS, icon: <img src={downloadIcon} alt="Download in XLS" /> },
        { label: 'Download in PDF', onClick: handlePDF, icon: <img src={downloadIcon} alt="Download in PDF" /> },
    ]

    if (onPrintRoster) {
        actions.push({
            label: 'Print Student Logins',
            onClick: onPrintRoster,
            icon: <img src={downloadIcon} alt="Print Student Logins" />,
        })
    }

    return <ActionDropdown actions={actions} className={styles.downloadDropdown} />
}

export const StudentActionDropdown = ({ onOpenStudent, className }: { onOpenStudent: () => void; className?: string }) => {
    return (
        <ActionDropdown actions={[
            { label: 'Open Lesson Details', onClick: onOpenStudent, icon: <img src={expandIcon} alt="Open Lesson Details" /> },
            { label: 'Download in XLS', onClick: () => console.log('Download in XLS'), icon: <img src={downloadIcon} alt="Download in XLS" /> },
            { label: 'Download in PDF', onClick: () => console.log('Download in PDF'), icon: <img src={downloadIcon} alt="Download in PDF" /> },
        ]} className={className ?? styles.studentActionDropdown}/>
    )
}

interface LessonActionDropdownProps {
    onDuplicateLesson: () => void
    onRenameLesson: () => void
    onDeleteLesson: () => void
    isDuplicating?: boolean
    isRenaming?: boolean
    isDeleting?: boolean
    hasGradedResults?: boolean
    opensUp?: boolean
}

export const LessonActionDropdown = ({ onDuplicateLesson, onRenameLesson, onDeleteLesson, isDuplicating = false, isRenaming = false, isDeleting = false, hasGradedResults = false, opensUp = false }: LessonActionDropdownProps) => {
    return (
        <ActionDropdown actions={[
            {
                label: isRenaming ? 'Renaming' : 'Rename',
                onClick: onRenameLesson,
                icon: <img src={editIcon} alt="Rename" />,
                disabled: isRenaming
            },
            {
                label: isDuplicating ? 'Duplicating' : 'Make a copy',
                onClick: onDuplicateLesson,
                icon: <img src={copyIcon} alt="Make a copy" />,
                disabled: isDuplicating
            },
            {
                label: hasGradedResults ? 'Delete (has grades)' : isDeleting ? 'Deleting' : 'Delete',
                onClick: onDeleteLesson,
                icon: <img src={trashIcon} alt="Delete" />,
                disabled: isDeleting || hasGradedResults
            },
        ]} className={opensUp ? styles.lessonActionDropdownUp : styles.lessonActionDropdown}/>
    )
}

/* Her `#classActions` menu (AllForOne_ClassroomDetail_v2.html), which her own
   comment introduces as: "Lessons, Download, Edit details and Archive used to
   be four separate controls competing with the filter pills. They live here
   now."

   Her items are Edit class details / Lessons and grades / Download as CSV /
   Download as printable HTML / Archive class. Two deliberate departures:

   - the download labels name OUR formats. The class export is XLS and PDF,
     not CSV and HTML, and copying her wording would have the menu describe a
     file the code does not produce.
   - Print Student Logins is kept. She has no equivalent, but it is a real
     action with no other entry point on this screen, and dropping it to match
     a mockup would remove a feature rather than restyle one.

   Her two separators are not reproduced: the shared ActionDropdown has no
   separator in its contract, and Archive already reads apart through
   `danger`. Not worth widening a shared brick's interface for a hairline. */
export const ClassActionsDropdown = ({
    onEditDetails,
    onLessonsAndGrades,
    onPrintRoster,
    onArchive,
}: {
    onEditDetails: () => void,
    onLessonsAndGrades: () => void,
    onPrintRoster: () => void,
    onArchive: () => void,
}) => {
    const classContext = useContext(ClassContext)
    return (
        <ActionDropdown actions={[
            { label: 'Edit class details', onClick: onEditDetails, icon: <img src={editIcon} alt="" /> },
            { label: 'Lessons and grades', onClick: onLessonsAndGrades, icon: <img src={copyIcon} alt="" /> },
            { label: 'Download in XLS', onClick: classContext?.handleDownloadXLS ?? (() => {}), icon: <img src={downloadIcon} alt="" /> },
            { label: 'Download in PDF', onClick: classContext?.handleDownloadPDF ?? (() => {}), icon: <img src={downloadIcon} alt="" /> },
            { label: 'Print Student Logins', onClick: onPrintRoster, icon: <img src={downloadIcon} alt="" /> },
            { label: 'Archive class', onClick: onArchive, icon: <img src={trashIcon} alt="" />, danger: true },
        ]} className={styles.updateClassActionDropdown} />
    )
}

